import { Badge } from '@mantine/core';
import { IconPencil, IconQrcode, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  resolveChecklistColor,
  resolveChecklistIcon,
} from 'src/Components/Checklists/checklistVisuals.helper';
import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useCreateChecklistExport } from 'src/Hooks/Mutations/useCreateChecklistExport';
import { useDeleteChecklist } from 'src/Hooks/Mutations/useDeleteChecklist';
import useFetchChecklists from 'src/Hooks/Queries/useFetchChecklists';
import { useCanCreate } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { usePermissionDeniedModal } from 'src/Hooks/Shared/usePermissionDeniedModal';

import styles from './ChecklistList.module.scss';

const ChecklistList = () => {
  const { data, isLoading } = useFetchChecklists();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { mutate: deleteChecklist } = useDeleteChecklist();

  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [exportUrl, setExportUrl] = useState<string | undefined>(undefined);
  const [exportName, setExportName] = useState<string | undefined>(undefined);

  const { mutate: exportChecklist } = useCreateChecklistExport({
    onSuccess: data => {
      setExportUrl(data.publicUrl);
      setShowQRCodeModal(true);
    },
  });

  const assetAboveQuotas = useIsAssetAboveQuotas();

  // Permission checks
  const { hasPermission: canModify, ownerName } = useCanCreate('checklist');
  const { hasPermission: canExport } = useCanCreate('entityExport');
  const showPermissionDeniedModal = usePermissionDeniedModal(ownerName);

  return (
    <>
      <QRCodeModal
        open={showQRCodeModal}
        url={exportUrl ?? ''}
        onClose={() => setShowQRCodeModal(false)}
        exportName={exportName ?? ''}
      />
      <DataTable
        withRowBorders={false}
        style={{ width: '100%', minHeight: '10rem' }}
        records={data}
        noRecordsText={t('checklist.table.empty', 'Aucune checklist')}
        fetching={isLoading}
        rowStyle={() => ({ minHeight: 54 })}
        onRowClick={row => navigate(`${row.record.id}/submissions`)}
        columns={[
          {
            accessor: 'title',
            title: '',
            render: value => {
              const Icon = resolveChecklistIcon(value.icon);
              const color = resolveChecklistColor(value.color);

              return (
                <div className={styles.titleRow}>
                  <div className={styles.iconBadge} style={{ backgroundColor: `${color}1f` }}>
                    <Icon size={16} color={color} />
                  </div>
                  <div className={styles.title}>{value.title}</div>
                  {value.templateKey && (
                    <Badge variant="outline" color="gray" size="sm" radius="sm">
                      {t('checklist.table.fromTemplate', 'Créée depuis un modèle')}
                    </Badge>
                  )}
                </div>
              );
            },
          },
          {
            accessor: 'actions',
            title: '',
            width: 50,
            hidden: isMobile,
            render: ({ id, title }) => (
              <RowActions
                actions={[
                  {
                    key: 'edit',
                    label: t('checklist.table.menu.edit', 'Modifier'),
                    icon: <IconPencil size={18} />,
                    disabled: assetAboveQuotas,
                    onClick: () => {
                      if (!canModify) {
                        showPermissionDeniedModal();
                        return;
                      }
                      navigate(`${id}/edit`);
                    },
                  },
                  {
                    key: 'export',
                    label: t('checklist.table.menu.export', 'Exporter'),
                    icon: <IconQrcode size={18} />,
                    hidden: !canExport,
                    onClick: () => {
                      setExportName(title);
                      exportChecklist([id]);
                    },
                  },
                  {
                    key: 'delete',
                    label: t('checklist.table.menu.delete', 'Supprimer'),
                    icon: <IconTrash size={18} />,
                    variant: 'danger',
                    onClick: () => {
                      if (!canModify) {
                        showPermissionDeniedModal();
                        return;
                      }
                      deleteChecklist(id);
                    },
                  },
                ]}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default ChecklistList;
